import Vue from 'vue'
import Router from 'vue-router'
import NotFoundView from '@/components/NotFoundView'
import Packages from '@/packages/Packages'
import AdminPayments from '@/payments/AdminPayments'
import RunnerPayments from '@/payments/RunnerPayments'
import RunnerPaymentPay from '@/payments/RunnerPaymentPay'
import RunnerPaymentReturn from '@/payments/RunnerPaymentReturn'

import Login from '@/auth/Login'
import PasswordReset from '@/auth/PasswordReset'
import PasswordResetConfirm from '@/auth/PasswordResetConfirm'
import RunnerSelfRegister from '@/auth/RunnerSelfRegister'
import RegistrationCompleted from '@/auth/RegistrationCompleted'
import AccountConfirmEmail from '@/auth/AccountConfirmEmail'
import AccountRunnerAdminConfirmEmail from '@/auth/AccountRunnerAdminConfirmEmail'
import AccountTrainerAdminConfirmEmail from '@/auth/AccountTrainerAdminConfirmEmail'
import ChangePassword from '@/auth/ChangePassword'

import AdminRunners from '@/runners/AdminRunners'
import TrainerRunners from '@/runners/TrainerRunners'
import RunnerData from '@/runners/RunnerData'
import RunnerApplicationDataPackage from '@/runners/RunnerApplicationDataPackage'
import RunnerApplicationDataTrainer from '@/runners/RunnerApplicationDataTrainer'
import RunnerApplicationDataFirst from '@/runners/RunnerApplicationDataFirst'
import RunnerApplicationDataSecond from '@/runners/RunnerApplicationDataSecond'
import Plan from '@/plan/Plan'
import PhysicalTests from '@/physical_tests/PhysicalTests'
import Results from '@/results/Results'
import Notifications from '@/notifications/Notifications'
import Races from '@/races/Races'

import Videos from '@/videos/Videos'
import Page from '@/pages/Page'

import AdminTrainers from '@/trainers/AdminTrainers'
import TrainerData from '@/trainers/TrainerData'
import TrainerDataPresentation from '@/trainers/TrainerDataPresentation'

import UnauthenticatedLayout from '@/layouts/UnauthenticatedLayout'
import AppLayout from '@/layouts/AppLayout'
import TrainerPanelLayout from '@/layouts/TrainerPanelLayout'
import RunnerPanelLayout from '@/layouts/RunnerPanelLayout'
import AdminPanelLayout from '@/layouts/AdminPanelLayout'

import Forum from '@/layouts/Forum'

import store from '@/store'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  scrollBehavior (to, from, savedPosition) {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/site-layout',
      name: 'unauthenticated',
      component: UnauthenticatedLayout,
      meta: { requiresAuth: false },
      children: [
        {
          path: '/accounts/login/',
          name: 'login',
          component: Login
        },
        {
          path: '/accounts/password-reset/',
          name: 'password-reset',
          component: PasswordReset
        },
        {
          path: '/accounts/password-reset-confirm/:uidb64/:token/',
          name: 'password-confirm-email',
          component: PasswordResetConfirm
        },
        {
          path: '/accounts/register-self/',
          name: 'register-self',
          component: RunnerSelfRegister
        },
        {
          path: '/accounts/registration-completed/',
          name: 'registration-completed',
          component: RegistrationCompleted
        },
        {
          path: '/accounts/account-confirm-email/runner_self/:token/',
          name: 'account-confirm-email',
          component: AccountConfirmEmail
        },
        {
          path: '/accounts/account-confirm-email/runner/:token/',
          name: 'account-runner-admin-confirm-email',
          component: AccountRunnerAdminConfirmEmail
        },
        {
          path: '/accounts/account-confirm-email/trainer/:token/',
          name: 'account-trainer-admin-confirm-email',
          component: AccountTrainerAdminConfirmEmail
        }
      ]
    },
    {
      path: '/',
      name: 'app',
      redirect: to => {
        if (!store.getters.isAuthenticated) {
          return {name: 'login'}
        }
        let authUser = store.getters.authUser
        if (authUser) {
          if (authUser.role_id === 'ADMIN') {
            return {name: 'runners-admin'}
          } else if (authUser.role_id === 'TRAINER') {
            return {name: 'trainer-runners', params: {id: authUser.id}}
          } else if (authUser.role_id === 'RUNNER') {
            return {name: 'runner-plan', params: {id: authUser.id}}
          }
        }
        return {name: 'login'}
      }
    },
    {
      path: '/my-profile',
      name: 'my-profile',
      redirect: to => {
        if (!store.getters.isAuthenticated) {
          return {name: 'login'}
        }
        let authUser = store.getters.authUser
        if (authUser) {
          if (authUser.role_id === 'ADMIN') {
            return {name: 'admin-change-password'}
          } else if (authUser.role_id === 'TRAINER') {
            return {name: 'trainer-data', params: {id: authUser.id}}
          } else if (authUser.role_id === 'RUNNER') {
            return {name: 'runner-data', params: {id: authUser.id}}
          }
        }
        return {name: 'login'}
      }
    },
    {
      path: '/app-layout',
      name: 'app-layout',
      component: AppLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '/admin/',
          name: 'admin-panel',
          components: {
            default: AdminPanelLayout
          },
          redirect: {name: 'runners-admin'},
          meta: { adminAuth: true },
          children: [
            {
              path: 'accounts/change-password/',
              name: 'admin-change-password',
              component: ChangePassword
            },
            {
              path: 'runners/',
              name: 'runners-admin',
              component: AdminRunners
            },
            {
              path: 'trainers/',
              name: 'trainers-admin',
              component: AdminTrainers
            },
            {
              path: 'packages/',
              name: 'packages',
              component: Packages
            },
            {
              path: 'videos/',
              name: 'admin-videos',
              component: Videos
            },
            {
              path: 'payments/',
              name: 'payments-admin',
              component: AdminPayments
            },
            {
              path: 'forum/',
              name: 'forum-admin',
              component: Forum
            },
            {
              path: 'pages/:slug/',
              name: 'pages-admin',
              component: Page
            }
          ]
        },
        {
          path: '/trainers/:id/',
          name: 'trainer-panel',
          components: {
            default: TrainerPanelLayout
          },
          meta: { trainerAuth: true },
          children: [
            {
              path: 'accounts/change-password/',
              name: 'trainer-change-password',
              component: ChangePassword
            },
            {
              path: 'profile/',
              name: 'trainer-data',
              component: TrainerData
            },
            {
              path: 'runners/',
              name: 'trainer-runners',
              component: TrainerRunners
            },
            {
              path: 'pages/:slug/',
              name: 'trainer-pages',
              component: Page
            },
            {
              path: 'notifications/',
              name: 'trainer-notifications',
              component: Notifications
            },
            {
              path: 'forum/',
              name: 'trainer-forum',
              component: Forum
            },
            {
              path: 'videos/',
              name: 'trainer-videos',
              component: Videos
            }
          ]
        },
        {
          path: '/runners/:id/',
          name: 'runner-panel-fluid',
          component: RunnerPanelLayout,
          props: { fluid: true },
          children: [
            {
              path: 'plan/',
              name: 'runner-plan',
              component: Plan
            }
          ]
        },
        {
          path: '/runners/:id/',
          name: 'runner-panel',
          component: RunnerPanelLayout,
          children: [
            {
              path: 'accounts/change-password/',
              name: 'runner-change-password',
              component: ChangePassword
            },
            {
              path: 'profile/',
              name: 'runner-data',
              component: RunnerData
            },
            {
              path: 'application-package-step/',
              name: 'runner-application-data-package',
              component: RunnerApplicationDataPackage
            },
            {
              path: 'application-trainer-step/',
              name: 'runner-application-data-trainer',
              component: RunnerApplicationDataTrainer
            },
            {
              path: 'application-first-step/',
              name: 'runner-application-data-first',
              component: RunnerApplicationDataFirst
            },
            {
              path: 'application-second-step/',
              name: 'runner-application-data-second',
              component: RunnerApplicationDataSecond
            },
            {
              path: 'trainer/',
              name: 'runner-trainer-data',
              component: TrainerDataPresentation
            },
            {
              path: 'hrmax/',
              name: 'runner-hrmax',
              component: PhysicalTests
            },
            {
              path: 'pages/:slug/',
              name: 'runner-pages',
              meta: { runnerAuth: true },
              component: Page
            },
            {
              path: 'progression/',
              name: 'runner-progression',
              component: Results
            },
            {
              path: 'races/',
              name: 'runner-races',
              component: Races
            },
            {
              path: 'payments/',
              name: 'runner-payments',
              component: RunnerPayments
            },
            {
              path: 'payments/:payment_id/pay/',
              name: 'runner-payments-pay',
              meta: { runnerAuth: true },
              component: RunnerPaymentPay
            },
            {
              path: 'payments/:payment_id/:status/',
              name: 'runner-payments-return',
              meta: { runnerAuth: true },
              component: RunnerPaymentReturn
            },
            {
              path: 'videos/',
              name: 'runner-videos',
              meta: { runnerAuth: true },
              component: Videos
            },
            {
              path: 'forum/',
              name: 'runner-forum',
              component: Forum
            },
            {
              path: 'notifications/',
              name: 'runner-notifications',
              meta: { runnerAuth: true },
              component: Notifications
            }
          ]
        },
        {
          path: '*',
          component: NotFoundView
        }
      ]
    }
  ]
})

const hasAccess = (to, role) => {
  if (role === 'ADMIN') {
    return true
  }
  if (to.matched.some(record => record.meta.adminAuth)) {
    return false
  } else if (to.matched.some(record => record.meta.trainerAuth)) {
    return role === 'TRAINER'
  } else if (to.matched.some(record => record.meta.runnerAuth)) {
    return role === 'RUNNER'
  } else {
    return true
  }
}

router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!store.getters.isAuthenticated) {
      return next({
        name: 'login',
        query: { redirect: to.fullPath }
      })
    }
    if (hasAccess(to, store.getters.authUser.role_id)) {
      return next()
    } else {
      return next({
        name: 'login',
        query: { redirect: to.fullPath }
      })
    }
  } else {
    next()
  }
})

export default router



// WEBPACK FOOTER //
// ./src/router/index.js